import { useQuery } from '@tanstack/react-query';
import { supabase } from '../lib/supabaseClient';
import type { FafAuditResponse } from '../types/faf-audit.types';

// Must match FAF_AUDIT_CACHE_VERSION in supabase/functions/faf-sanction-audit/index.ts.
// Cached rows whose summary.cacheVersion differs are treated as stale and regenerated,
// so deploying a new EF shape (e.g. the Arabic *_ar fields) auto-busts old Latin caches.
//
// v3: le rapprochement lui-même a changé (fin du match sur le corps de licence
// sans l'année, licences bornées à la saison auditée). La FORME du payload est
// identique, mais son CONTENU ne l'est plus : sans bump, les lignes calculées
// avant le déploiement resteraient servies indéfiniment.
const FAF_AUDIT_CACHE_VERSION = 3;

/** Fire-and-forget: write FAF audit result to cache */
function writeFafAuditCache(leagueId: string, season: string, result: FafAuditResponse) {
    supabase.rpc("upsert_faf_audit_cache", {
        p_league_id: leagueId,
        p_season: season,
        p_total_count: result.summary?.issueCount ?? 0,
        p_summary: result.summary ?? {},
        p_matches: result.matches ?? [],
    }).then(({ error }) => {
        if (error) console.warn("[faf-audit-cache] write failed:", error.message);
    });
}

export function useFafSanctionAudit(leagueId: string | null, season: string | null) {
  return useQuery({
    queryKey: ['fafSanctionAudit', { leagueId, season }],
    enabled: !!leagueId && !!season,
    staleTime: 5 * 60 * 1000,
    queryFn: async () => {
      if (!leagueId || !season) return null;

      // 1. Try cache first (~50ms)
      const { data: cached } = await supabase
          .from("faf_audit_cache")
          .select("summary, matches, stale")
          .eq("league_id", leagueId)
          .eq("season", season)
          .maybeSingle();

      // Treat a cache row as usable only if it is not flagged stale AND its payload
      // shape matches the current version (so older Latin-only rows regenerate).
      const cacheFresh =
        cached && !cached.stale &&
        (cached.summary as { cacheVersion?: number } | null)?.cacheVersion === FAF_AUDIT_CACHE_VERSION;

      if (cacheFresh) {
        return {
          summary: cached.summary,
          matches: Array.isArray(cached.matches) ? cached.matches : [],
        } as FafAuditResponse;
      }

      // 2. Cache miss / stale / outdated shape — call edge function
      const { data, error } = await supabase.functions.invoke('faf-sanction-audit', {
        body: { leagueId, season },
      });
      if (error) {
        // If EF fails but we have stale cache, return stale data
        if (cached) {
          return {
            summary: cached.summary,
            matches: Array.isArray(cached.matches) ? cached.matches : [],
          } as FafAuditResponse;
        }
        const ctx = (error as any).context;
        if (ctx instanceof Response) {
          try {
            const body = await ctx.clone().json();
            if (body?.error) throw new Error(body.error);
          } catch (parseErr) {
            if (parseErr instanceof Error && parseErr.message !== error.message) throw parseErr;
          }
        }
        throw error;
      }

      const result = data as FafAuditResponse;

      // 3. Write to cache (fire-and-forget)
      writeFafAuditCache(leagueId, season, result);

      return result;
    },
  });
}

/**
 * Lightweight count-only query: reads `total_count` from the cache table.
 * Used for tab badges. On a cache miss, the edge function itself does not
 * persist a cache row — so we compute once via `faf-sanction-audit` and
 * write the result through the same `writeFafAuditCache` helper the full
 * audit hook uses, so a missing row is no longer indistinguishable from
 * zero and the next read hits the warmed cache. Never throws — the badge is
 * "soft" info and silently falls back to 0 on any edge-function failure.
 *
 * Le badge applique les MÊMES conditions de fraîcheur que l'audit complet
 * (`stale`, `cacheVersion`) : sinon une ligne invalidée continuait à servir son
 * `total_count`, et le badge annonçait un nombre d'anomalies que la page
 * n'affichait plus.
 */
export function useFafSanctionAuditCount(leagueId: string | null, season: string | null) {
    return useQuery({
        queryKey: ['fafSanctionAudit', 'count', { leagueId, season }],
        enabled: !!leagueId && !!season,
        staleTime: 60 * 1000,
        queryFn: async (): Promise<number> => {
            if (!leagueId || !season) return 0;
            const { data } = await supabase
                .from("faf_audit_cache")
                .select("total_count, summary, stale")
                .eq("league_id", leagueId)
                .eq("season", season)
                .maybeSingle();
            const fresh =
                data && !data.stale &&
                (data.summary as { cacheVersion?: number } | null)?.cacheVersion === FAF_AUDIT_CACHE_VERSION;
            if (fresh) return (data.total_count as number | undefined) ?? 0;

            // Un-warmed (league, season): compute once via the edge function and
            // persist the cache ourselves (the EF response has no persistence
            // side-effect of its own).
            try {
                const { data: computed, error } = await supabase.functions.invoke('faf-sanction-audit', {
                    body: { leagueId, season },
                });
                if (error) return 0;
                const result = computed as FafAuditResponse;
                writeFafAuditCache(leagueId, season, result);
                return result?.summary?.issueCount ?? 0;
            } catch {
                return 0; // badge stays "soft" — never throw for a badge
            }
        },
    });
}
